import { useAction } from "convex/react";
import { Interweave, type Node } from "interweave";
import { EmailMatcher, UrlMatcher } from "interweave-autolink";
import { ExternalLinkIcon } from "lucide-react";
import { type ReactNode, useState } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button, buttonVariants } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { formatPPpp } from "./date-format";
import { MailAttachments } from "./mail-attachments";
import type { MailMessageDetail } from "./types";

interface MailDisplayProps {
	message: MailMessageDetail | null;
	isLoading: boolean;
}

const ALLOWED_WEBLINK_PREFIXES = [
	"https://mail.google.com/",
	"https://outlook.office.com/",
	"https://outlook.office365.com/",
	"https://outlook.live.com/",
];

function safeWebLink(link: string | undefined): string | null {
	if (!link) return null;
	return ALLOWED_WEBLINK_PREFIXES.some((p) => link.startsWith(p)) ? link : null;
}

const SAFE_TAGS = [
	"p",
	"br",
	"div",
	"span",
	"a",
	"ul",
	"ol",
	"li",
	"blockquote",
	"pre",
	"code",
	"strong",
	"em",
	"b",
	"i",
	"u",
	"s",
	"sub",
	"sup",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"table",
	"thead",
	"tbody",
	"tfoot",
	"tr",
	"td",
	"th",
	"img",
	"hr",
	"font",
];

const EXTERNAL_IMG_REGEX = /<img[^>]+src\s*=\s*["']?https?:/i;
const SAFE_LINK_PROTOCOL = /^(https?:|mailto:)/i;

function extractBodyFragment(html: string): string {
	const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
	if (bodyMatch) return bodyMatch[1];
	return html
		.replace(/<!DOCTYPE[^>]*>/gi, "")
		.replace(/<\/?html[^>]*>/gi, "")
		.replace(/<head[\s\S]*?<\/head>/gi, "")
		.replace(/<\/?body[^>]*>/gi, "");
}

function initials(name: string): string {
	const parts = name.split(/[\s@.]+/).filter(Boolean);
	const picked = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "");
	return picked.join("") || "?";
}

function makeTransform(
	showImages: boolean,
): (node: HTMLElement, children: Node[]) => ReactNode {
	return (node, children) => {
		const tag = node.tagName.toLowerCase();
		if (tag === "img") {
			const src = node.getAttribute("src") ?? "";
			const isExternalHttp = /^https?:/i.test(src);
			const isDataImage = /^data:image\//i.test(src);
			if (!isExternalHttp && !isDataImage) return null;
			if (isExternalHttp && !showImages) return null;
			const alt = node.getAttribute("alt") ?? "";
			return (
				<img
					src={src}
					alt={alt}
					referrerPolicy="no-referrer"
					loading="lazy"
					className="max-w-full"
				/>
			);
		}
		if (tag === "a") {
			const href = node.getAttribute("href") ?? "";
			if (!SAFE_LINK_PROTOCOL.test(href)) {
				return <>{children as ReactNode}</>;
			}
			return (
				<a
					href={href}
					target="_blank"
					rel="noopener noreferrer nofollow"
					className="underline"
				>
					{children as ReactNode}
				</a>
			);
		}
		return undefined;
	};
}

function EmailBody({ message }: { message: MailMessageDetail }) {
	const [showImages, setShowImages] = useState(false);
	const bodyHtml = message.bodyHtml;

	if (bodyHtml && bodyHtml.length > 0) {
		const fragment = extractBodyFragment(bodyHtml);
		const hasExternal = EXTERNAL_IMG_REGEX.test(fragment);
		const transform = makeTransform(showImages);
		return (
			<div className="flex h-full flex-col">
				{hasExternal && !showImages && (
					<div className="flex items-center justify-between border-b bg-muted px-4 py-2 text-xs">
						<span className="text-muted-foreground">
							Remote images are blocked by default.
						</span>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowImages(true)}
						>
							Load images
						</Button>
					</div>
				)}
				<ScrollArea className="min-h-0 flex-1">
					<div className="p-4 text-sm">
						<Interweave
							content={fragment}
							allowList={SAFE_TAGS}
							transform={transform}
							tagName="div"
						/>
					</div>
				</ScrollArea>
			</div>
		);
	}

	return (
		<ScrollArea className="min-h-0 flex-1">
			<div className="whitespace-pre-wrap p-4 text-sm text-muted-foreground">
				<Interweave
					content={message.snippet || "(empty body)"}
					noHtml
					matchers={[new UrlMatcher("url"), new EmailMatcher("email")]}
				/>
			</div>
		</ScrollArea>
	);
}

export function MailDisplay({ message, isLoading }: MailDisplayProps) {
	const getAttachmentUrl = useAction(
		api.mail.attachments.getAttachmentDownloadUrl,
	);
	const [downloadingId, setDownloadingId] =
		useState<Id<"mailAttachments"> | null>(null);

	const handleDownload = async (
		attachmentId: Id<"mailAttachments">,
		filename: string,
	) => {
		setDownloadingId(attachmentId);
		try {
			const result = await getAttachmentUrl({ attachmentId });
			const a = document.createElement("a");
			a.href = result.url;
			a.download = filename || result.name;
			a.target = "_blank";
			a.rel = "noopener noreferrer";
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} catch (err) {
			console.error("Failed to download attachment", err);
		} finally {
			setDownloadingId(null);
		}
	};

	if (isLoading) {
		return (
			<div className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground">
				Loading…
			</div>
		);
	}

	if (!message) {
		return (
			<div className="flex h-full items-center justify-center p-8 text-center text-sm text-muted-foreground">
				No message selected
			</div>
		);
	}

	const senderName = message.fromName ?? message.fromEmail;
	const providerLink = safeWebLink(message.webLink);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex items-start p-4">
				<div className="flex items-start gap-4 text-sm">
					<Avatar>
						<AvatarFallback>{initials(senderName)}</AvatarFallback>
					</Avatar>
					<div className="grid gap-1">
						<div className="font-semibold">{senderName}</div>
						<div className="text-xs">
							<span className="font-medium">From:</span> {message.fromEmail}
						</div>
						{message.toRecipients.length > 0 && (
							<div className="text-xs">
								<span className="font-medium">To:</span>{" "}
								{message.toRecipients.map((r) => r.name ?? r.email).join(", ")}
							</div>
						)}
						<div className="text-xs font-medium">
							{message.subject || "(no subject)"}
						</div>
					</div>
				</div>
				<div className="ml-auto flex items-center gap-2">
					<div className="text-xs text-muted-foreground">
						{formatPPpp(new Date(message.receivedAt))}
					</div>
					{providerLink && (
						<a
							href={providerLink}
							target="_blank"
							rel="noopener noreferrer"
							title="Open in provider"
							className={cn(buttonVariants({ variant: "ghost", size: "icon" }))}
						>
							<ExternalLinkIcon className="h-4 w-4" />
						</a>
					)}
				</div>
			</div>
			<Separator />
			{message.attachments.length > 0 && (
				<>
					<MailAttachments
						attachments={message.attachments}
						downloadingId={downloadingId}
						onDownload={handleDownload}
					/>
					<Separator />
				</>
			)}
			<EmailBody key={message._id} message={message} />
			{message.bodyTruncated && (
				<div className="border-t bg-muted p-2 text-center text-xs text-muted-foreground">
					Body was truncated — open in provider for the full message.
				</div>
			)}
		</div>
	);
}
